import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import SidePanel from '../util/SidePanel';
import PeopleIcon from '@mui/icons-material/People';
import ProfileCard from '../modal/ProfileCard';
import UserAvatar from '../util/UserAvatar';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  job_title?: string;
  avatarUrl?: string;
  createdAt: string;
}

interface Props {
  auth: boolean;
  baseUrl: string;
  access: any;
}


function daysAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
}

const RecentEmployeesCard: React.FC<Props> = ({ auth, baseUrl, access }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Employee | null>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const fetchingUsers = React.useRef(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get('users/recent');
      setEmployees(data);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { if (auth) fetchData(); }, [auth, fetchData]);

  const handleOpen = useCallback(async (emp: Employee) => {
    setSelected(emp);
    if (allUsers.length === 0 && !fetchingUsers.current) {
      fetchingUsers.current = true;
      try {
        const { data } = await axios.get('users');
        setAllUsers(data);
      } catch { }
      finally { fetchingUsers.current = false; }
    }
  }, [allUsers.length]);

  return (
    <>
      <div className="dashboard-card" style={{ padding: '14px 14px 10px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
          <PeopleIcon style={{ fontSize: 16, color: '#a78bfa' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            New Employees
          </span>
        </div>

        {loading ? (
          <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: '8px 0' }}>Loading...</div>
        ) : employees.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: '8px 0' }}>No employees</div>
        ) : (
          // fills the stretched card; scrolls when there are more people than fit
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
            {employees.map(emp => (
              <button
                key={emp.id}
                type="button"
                onClick={() => handleOpen(emp)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '4px 0', textAlign: 'left', width: '100%',
                  borderRadius: 6,
                }}
                className="hover:bg-[var(--btn-user-form)]"
              >
                <UserAvatar user={emp} baseUrl={baseUrl} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {emp.first_name} {emp.last_name}
                  </div>
                  {emp.job_title && (
                    <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {emp.job_title}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>
                  {daysAgo(emp.createdAt)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Profile drawer */}
      {/* ProfileCard opens in peer mode here (no isSelf/hrMode): basic fields only → half the full 760px width */}
      <SidePanel open={!!selected} onClose={() => setSelected(null)} width={380}>
        {selected && (
          <ProfileCard
            users={allUsers}
            selectedUser={selected}
            setShow={() => setSelected(null)}
            refreshUsers={fetchData}
            refreshUserHeder={() => {}}
            access={access}
            baseUrl={baseUrl}
          />
        )}
      </SidePanel>
    </>
  );
};

export default RecentEmployeesCard;
